import React, { useEffect, useState, useCallback } from 'react';
import { useUser } from '@clerk/clerk-react';
import { useNavigate } from 'react-router-dom';
import type { Venue } from '../../types/venue';
import { fetchVenueByUser } from '../../utils/venueApi';
import OverviewPanel from './dashboard/OverviewPanel';
import GuestAnalyticsPanel from './dashboard/GuestAnalyticsPanel';
import SurveyResultsPanel from './dashboard/SurveyResultsPanel';
import PunchCardSettingsPanel from './dashboard/PunchCardSettingsPanel';
import MenuSettingsPanel from './dashboard/MenuSettingsPanel';
import ReviewSettingsPanel from './dashboard/ReviewSettingsPanel';
import WifiSettingsPanel from './dashboard/WifiSettingsPanel';
import RedemptionPanel from './dashboard/RedemptionPanel';
import QrRegeneratePanel from './dashboard/QrRegeneratePanel';
import Button from '../ui/Button';
import { ExternalLink, Printer } from 'lucide-react';

const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'analytics', label: 'Analytics' },
  { key: 'surveys', label: 'Surveys' },
  { key: 'punchcard', label: 'Punch Card' },
  { key: 'menu', label: 'Menu' },
  { key: 'reviews', label: 'Reviews' },
  { key: 'wifi', label: 'Wi-Fi' },
  { key: 'redemption', label: 'Redemption' },
  { key: 'qr', label: 'QR Codes' },
];

const VenueDashboard: React.FC = () => {
  const { user } = useUser();
  const navigate = useNavigate();
  const [venue, setVenue] = useState<Venue | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    document.title = 'Venue Dashboard - WhoBrew';
  }, []);

  const loadVenue = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchVenueByUser(user.id);
      if (!data) {
        navigate('/venue/setup');
        return;
      }
      setVenue(data);
    } catch (err: any) {
      if (err.message?.includes('404')) {
        navigate('/venue/setup');
        return;
      }
      setError(err.message || 'Failed to load venue data');
    } finally {
      setLoading(false);
    }
  }, [user, navigate]);

  useEffect(() => {
    if (user) loadVenue();
  }, [user, loadVenue]);

  const handleVenueUpdate = (updated: Venue) => {
    setVenue(updated);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <div className="border-b border-coffee-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
            <div className="animate-pulse space-y-3">
              <div className="h-3 bg-coffee-100 rounded w-24" />
              <div className="h-8 bg-coffee-100 rounded w-48" />
              <div className="h-4 bg-coffee-100 rounded w-36" />
            </div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="animate-pulse space-y-3">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-16 rounded-xl bg-coffee-50" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
            <p className="text-red-700 mb-4 text-sm">{error}</p>
            <Button variant="primary" onClick={loadVenue}>Retry</Button>
          </div>
        </div>
      </div>
    );
  }

  if (!venue) return null;

  const venueUrl = `https://whobrew.us/v/${venue.slug}`;

  return (
    <div className="min-h-screen bg-white text-coffee-900">
      {/* Page header */}
      <div className="border-b border-coffee-100 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="eyebrow mb-1.5">Venue Dashboard</div>
              <h1 className="text-display text-3xl sm:text-4xl text-coffee-900">{venue.name}</h1>
              <p className="mt-1 font-mono text-sm text-coffee-500">
                whobrew.us/v/{venue.slug}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0 pt-1">
              <Button
                variant="secondary"
                size="sm"
                href={venueUrl}
                target="_blank"
                rel="noopener noreferrer"
                leadingIcon={<ExternalLink className="w-3.5 h-3.5" />}
              >
                View as guest
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setActiveTab('qr')}
                leadingIcon={<Printer className="w-3.5 h-3.5" />}
              >
                Print QR
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Sticky tab bar */}
      <div className="border-b border-coffee-100 bg-white sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="overflow-x-auto">
            <div
              className="flex gap-1 min-w-max p-1 my-2 bg-coffee-50 border border-coffee-100 rounded-xl w-fit"
              role="tablist"
              aria-label="Dashboard tabs"
            >
              {TABS.map((tab) => (
                <button
                  key={tab.key}
                  role="tab"
                  aria-selected={activeTab === tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${
                    activeTab === tab.key
                      ? 'bg-coffee-900 text-white'
                      : 'text-coffee-500 hover:text-coffee-800 hover:bg-coffee-100'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Panel content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" role="tabpanel">
        {activeTab === 'overview' && <OverviewPanel venue={venue} />}
        {activeTab === 'analytics' && <GuestAnalyticsPanel venue={venue} />}
        {activeTab === 'surveys' && <SurveyResultsPanel venue={venue} />}
        {activeTab === 'punchcard' && <PunchCardSettingsPanel venue={venue} onUpdate={handleVenueUpdate} />}
        {activeTab === 'menu' && <MenuSettingsPanel venue={venue} onUpdate={handleVenueUpdate} />}
        {activeTab === 'reviews' && <ReviewSettingsPanel venue={venue} onUpdate={handleVenueUpdate} />}
        {activeTab === 'wifi' && <WifiSettingsPanel venue={venue} onUpdate={handleVenueUpdate} />}
        {activeTab === 'redemption' && <RedemptionPanel venue={venue} />}
        {activeTab === 'qr' && <QrRegeneratePanel venue={venue} />}
      </div>
    </div>
  );
};

export default VenueDashboard;
